import { cx } from '../../lib/cx.js';
import { formatCount } from '../../lib/format.js';

export interface BarRow {
  label: string;
  value: number;
  /** Optional second line under the label (source type, entity). */
  sublabel?: string;
  color?: string;
  href?: string;
}

export interface BarListProps {
  rows: BarRow[];
  /** Bars are capped at 24px; the band keeps its leftover as air. */
  barHeight?: number;
  className?: string;
  emptyLabel?: string;
  valueSuffix?: string;
}

/**
 * Horizontal bar list — the right form for "rank by magnitude": one baseline,
 * labelled rows, the value at the tip, a 4-px rounded data end and a surface
 * gap between neighbours. Each row is also a link when `href` is given.
 *
 * A rank list is **one series**, so every bar wears the same hue. Colouring
 * each row differently would burn the only free channel on information the bar
 * length already carries, and a per-row hue implies an identity the rows do not
 * have. Pass `row.color` only where colour genuinely means something (the
 * severity breakdown, where it is the severity token).
 */
export function BarList({
  rows,
  barHeight = 8,
  className,
  emptyLabel = 'Nothing recorded in this period',
  valueSuffix,
}: BarListProps) {
  if (rows.length === 0) {
    return (
      <p
        className={cx(
          'rounded-[var(--radius-md)] border border-dashed border-[var(--border)] px-3 py-6 text-center text-[var(--text-base)] text-[var(--text-3)]',
          className,
        )}
      >
        {emptyLabel}
      </p>
    );
  }
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className={cx('flex flex-col gap-2.5', className)}>
      {rows.map((row, i) => {
        const pct = Math.max(1.5, (row.value / max) * 100);
        const content = (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-[var(--text-base)] text-[var(--text)]">
                {row.label}
              </span>
              <span className="tnum shrink-0 text-[var(--text-base)] font-medium text-[var(--text)]">
                {formatCount(row.value)}
                {valueSuffix ? <span className="text-[var(--text-3)]"> {valueSuffix}</span> : null}
              </span>
            </div>
            {row.sublabel && (
              <p className="truncate text-[var(--text-sm)] text-[var(--text-3)]">{row.sublabel}</p>
            )}
            <div
              className="mt-1 w-full overflow-hidden rounded-[var(--radius-pill)] bg-[var(--surface-2)]"
              style={{ height: Math.min(barHeight, 24) }}
            >
              <div
                className="h-full rounded-r-[4px] transition-[width] duration-[var(--dur-base)] ease-[var(--ease)]"
                style={{
                  width: `${pct}%`,
                  backgroundColor: row.color ?? 'var(--chart-1)',
                }}
              />
            </div>
          </>
        );
        return (
          <li key={`${row.label}-${i}`}>
            {row.href ? (
              <a
                href={row.href}
                className="block rounded-[var(--radius-sm)] px-1 py-0.5 hover:bg-[var(--surface-2)]"
              >
                {content}
              </a>
            ) : (
              <div className="px-1 py-0.5">{content}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
